import { CompleteKundali, Language, PlanetName, ZodiacSign } from '../../types/kundali';
import { DayHoraSchedule, HoraPeriod, HoraSchedulerResult, RemedyScheduleAlignment } from '../../types/v518';
import { getDailyPanchangaFactors } from '../muhurtaEngine';
import { formatDate, formatTime } from '../dateUtils';
import { evaluateParashariLordship } from '../remediesBankEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.18 — REAL-TIME HORA & REMEDY SCHEDULER ENGINE (V518-05)
   Chaldean Planetary Hora Calculation & Optimal Remedial Alignment
   ========================================================================== */

const CHALDEAN_HORA_ORDER: PlanetName[] = [
  'Sun', 'Venus', 'Mercury', 'Moon', 'Saturn', 'Jupiter', 'Mars'
];

const DAY_LORDS: PlanetName[] = [
  'Sun',      // 0: Sunday
  'Moon',     // 1: Monday
  'Mars',     // 2: Tuesday
  'Mercury',  // 3: Wednesday
  'Jupiter',  // 4: Thursday
  'Venus',    // 5: Friday
  'Saturn'    // 6: Saturday
];

const DAY_NAMES_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_NAMES_HI = ['रविवार', 'सोमवार', 'मंगलवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];

const PLANET_NAMES_HI: Record<PlanetName, string> = {
  Sun: 'सूर्य',
  Moon: 'चंद्र',
  Mars: 'मंगल',
  Mercury: 'बुध',
  Jupiter: 'गुरु / बृहस्पति',
  Venus: 'शुक्र',
  Saturn: 'शनि',
  Rahu: 'राहु',
  Ketu: 'केतु',
  Ascendant: 'लग्न'
};

const HORA_THEMES: Record<PlanetName, {
  themeEn: string;
  themeHi: string;
  activityEn: string;
  activityHi: string;
  categories: string[];
}> = {
  Sun: {
    themeEn: 'Authority, Leadership, Vitality, Government Matters & Executive Decisions',
    themeHi: 'प्रशासनिक कार्य, नेतृत्व, सरकारी आवेदन एवं आत्मबल संवर्धन',
    activityEn: 'Meet superiors, apply for administrative approvals, start solar vitality rituals.',
    activityHi: 'उच्चाधिकारियों से भेंट, सरकारी कार्य एवं सूर्य नमस्कार/गायत्री जप।',
    categories: ['Ruby Gemstone', 'Surya Mantra', 'Aditya Hridaya Stotram', 'Vitality Rituals']
  },
  Moon: {
    themeEn: 'Mind, Emotional Wellness, Public Engagement, Water & Domestic Harmony',
    themeHi: 'मानसिक शांति, भावनात्मक कल्याण, जनसंपर्क एवं पारिवारिक सुख',
    activityEn: 'Public relations, creative writing, silver/pearl rituals, emotional healing.',
    activityHi: 'रचनात्मक कार्य, जनसेवा, मोती/चांदी धारण एवं मानसिक शांति ध्यान।',
    categories: ['Pearl Gemstone', 'Chandra Mantra', 'Sound Frequency 432Hz', 'Silver Donation']
  },
  Mars: {
    themeEn: 'Courage, Property, Engineering, Sports, Surgery & Vigorous Enterprise',
    themeHi: 'साहस, भूमि-भवन, तकनीकी कार्य, खेलकूद एवं ऊर्जावान प्रयास',
    activityEn: 'Land dealings, dispute resolutions, surgical procedures, physical training.',
    activityHi: 'भूमि सौदा, तकनीकी शुरुआत, व्यायाम एवं हनुमान चालीसा पाठ।',
    categories: ['Red Coral', 'Mangal Mantra', 'Hanuman Chalisa', 'Blood Donation']
  },
  Mercury: {
    themeEn: 'Trade, Finance, Analytics, Learning, Communications & Contract Signing',
    themeHi: 'व्यापार, लेखा-जोखा, शिक्षा, संचार एवं अनुबंध हस्ताक्षर',
    activityEn: 'Accounting audits, publishing, signing agreements, commercial transactions.',
    activityHi: 'व्यापारिक समझौते, नई विद्या का प्रारंभ, संचार एवं पन्ना धारण।',
    categories: ['Emerald Gemstone', 'Budh Mantra', 'Vishnu Sahasranama', 'Green Gifting']
  },
  Jupiter: {
    themeEn: 'Wisdom, Higher Learning, Legal Counsel, Wealth Investments & Dharma',
    themeHi: 'ज्ञान, उच्च शिक्षा, कानूनी परामर्श, धार्मिक अनुष्ठान एवं निवेश',
    activityEn: 'Commence higher education, major financial planning, spiritual ceremonies.',
    activityHi: 'उच्च शिक्षा आरंभ, निवेश परामर्श, गुरु पूजन एवं पुखराज धारण।',
    categories: ['Yellow Sapphire', 'Guru Mantra', 'Brihaspati Stotram', 'Turmeric Fasting']
  },
  Venus: {
    themeEn: 'Creativity, Romance, Luxury Purchases, Arts, Design & Vehicle Deals',
    themeHi: 'सृजन, प्रेम संबंध, कला, सौंदर्य, विलासिता एवं वाहन क्रय',
    activityEn: 'Artistic launches, luxury retail shopping, matrimonial discussions, design work.',
    activityHi: 'कलात्मक कार्य, विवाह वार्ता, आभूषण/वाहन क्रय एवं शुक्र बीज मंत्र।',
    categories: ['Diamond / Opal', 'Shukra Mantra', 'Sound Therapy 528Hz', 'White Charity']
  },
  Saturn: {
    themeEn: 'Discipline, Deep Research, Industrial Planning, Grounding & Debt Elimination',
    themeHi: 'अनुशासन, गहन शोध, औद्योगिक कार्य, ऋण मुक्ति एवं स्थिरता',
    activityEn: 'Long-term infrastructure, heavy machinery setup, seva/charity for workers.',
    activityHi: 'दीर्घकालिक योजनाएं, जनसेवा, तेल दान एवं शनि शांति जप।',
    categories: ['Blue Sapphire', 'Shani Mantra', 'Dasharatha Shani Stotram', 'Black Sesame Seva']
  },
  Rahu: {
    themeEn: 'Innovation, Strategy, Tech Exploration & Disruption',
    themeHi: 'नवाचार, रणनीतिक शोध एवं तकनीकी अन्वेषण',
    activityEn: 'AI research, global communications.',
    activityHi: 'वैश्विक अनुसंधान एवं रणनीतिक योजना।',
    categories: ['Hessonite Gemstone', 'Rahu Kavacham', 'Sound Therapy 108Hz']
  },
  Ketu: {
    themeEn: 'Spiritual Sadhana, Meditation, Detachment & Root Analytics',
    themeHi: 'आत्म-अन्वेषण, ध्यान, वैराग्य एवं गहन विश्लेषण',
    activityEn: 'Meditation, inner reflection, coding root algorithms.',
    activityHi: 'ध्यान, प्राणायाम एवं आत्म-चिंतन।',
    categories: ['Cat\'s Eye', 'Ketu Mantra', 'Maha Mrityunjaya']
  },
  Ascendant: {
    themeEn: 'Foundational Energy',
    themeHi: 'मूल ऊर्जा',
    activityEn: 'General focus',
    activityHi: 'सामान्य ध्यान',
    categories: ['Lagna Meditation']
  }
};

/**
 * Builds a 24-hour Hora sequence for a specific target date
 */
function buildDayHoraSchedule(
  targetDate: Date,
  lat: number,
  lon: number
): DayHoraSchedule {
  const panchang = getDailyPanchangaFactors(targetDate, lat, lon);
  const sunrise = panchang.sunrise;
  const sunset = panchang.sunset;

  // Next sunrise is approx 24h from today's sunrise
  const nextSunrise = new Date(sunrise.getTime() + 24 * 60 * 60 * 1000);

  const dayDurationMs = sunset.getTime() - sunrise.getTime();
  const nightDurationMs = nextSunrise.getTime() - sunset.getTime();

  const dayHoraMs = dayDurationMs / 12;
  const nightHoraMs = nightDurationMs / 12;

  const dayOfWeekIdx = targetDate.getDay();
  const dayLord = DAY_LORDS[dayOfWeekIdx];
  const startLordIdx = (CHALDEAN_HORA_ORDER || []).indexOf(dayLord);

  const horas: HoraPeriod[] = [];
  const now = new Date();

  // 12 Day Horas
  for (let i = 0; i < 12; i++) {
    const horaPlanet = CHALDEAN_HORA_ORDER[(startLordIdx + i) % 7];
    const startTime = new Date(sunrise.getTime() + i * dayHoraMs);
    const endTime = new Date(startTime.getTime() + dayHoraMs);
    const isCurrent = now >= startTime && now < endTime;

    const theme = HORA_THEMES[horaPlanet] || HORA_THEMES.Sun;

    horas.push({
      horaIndex: i,
      isDayHora: true,
      planet: horaPlanet,
      planetHi: PLANET_NAMES_HI[horaPlanet] || horaPlanet,
      startTime: formatTime(startTime, false),
      endTime: formatTime(endTime, false),
      isCurrent,
      governingThemeEn: theme.themeEn,
      governingThemeHi: theme.themeHi,
      recommendedActivityEn: theme.activityEn,
      recommendedActivityHi: theme.activityHi,
      alignedRemedyCategories: theme.categories,
      suitabilityScore: 85
    });
  }

  // 12 Night Horas
  for (let j = 0; j < 12; j++) {
    const horaPlanet = CHALDEAN_HORA_ORDER[(startLordIdx + 12 + j) % 7];
    const startTime = new Date(sunset.getTime() + j * nightHoraMs);
    const endTime = new Date(startTime.getTime() + nightHoraMs);
    const isCurrent = now >= startTime && now < endTime;

    const theme = HORA_THEMES[horaPlanet] || HORA_THEMES.Sun;

    horas.push({
      horaIndex: 12 + j,
      isDayHora: false,
      planet: horaPlanet,
      planetHi: PLANET_NAMES_HI[horaPlanet] || horaPlanet,
      startTime: formatTime(startTime, false),
      endTime: formatTime(endTime, false),
      isCurrent,
      governingThemeEn: theme.themeEn,
      governingThemeHi: theme.themeHi,
      recommendedActivityEn: theme.activityEn,
      recommendedActivityHi: theme.activityHi,
      alignedRemedyCategories: theme.categories,
      suitabilityScore: 75
    });
  }

  const dateStr = formatDate(targetDate);
  const srStr = formatTime(sunrise, false);
  const ssStr = formatTime(sunset, false);

  return {
    date: dateStr,
    dayOfWeek: DAY_NAMES_EN[dayOfWeekIdx],
    dayOfWeekHi: DAY_NAMES_HI[dayOfWeekIdx],
    dayLord,
    sunrise: srStr,
    sunset: ssStr,
    horas
  };
}

export function generateHoraRemedySchedule(
  kundali: CompleteKundali,
  lang: Language = 'en'
): HoraSchedulerResult {
  const isHi = lang === 'hi';
  const lat = kundali.birthDetails.latitude || 28.6139;
  const lon = kundali.birthDetails.longitude || 77.2090;

  const today = new Date();
  const todaySchedule = buildDayHoraSchedule(today, lat, lon);

  // 7-day schedule
  const sevenDaySchedule: DayHoraSchedule[] = [];
  for (let d = 0; d < 7; d++) {
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + d);
    sevenDaySchedule.push(buildDayHoraSchedule(targetDate, lat, lon));
  }

  // Find currently active hora or default to first hora
  let currentActiveHora = todaySchedule.horas.find(h => h.isCurrent) || todaySchedule.horas[0];

  // Align primary chart remedies with optimal Day & Hora slots
  const alignedRemedies: RemedyScheduleAlignment[] = [];

  // Extract from kundali.gemstones or primary planets needing strengthening
  // Extract Lagna sign for Parashari functional evaluation
  const lagnaSign: ZodiacSign = (
    kundali.charts?.D1?.houses?.find(h => h.houseNumber === 1)?.sign ||
    kundali.houses?.[0]?.sign ||
    kundali.planets?.find(p => p.name === 'Ascendant')?.sign ||
    (kundali.birthDetails as any)?.ascendantSign ||
    'Aries'
  ) as ZodiacSign;

  const keyPlanets: PlanetName[] = ['Jupiter', 'Sun', 'Mercury', 'Venus', 'Saturn', 'Mars', 'Moon'];

  keyPlanets.forEach((planet, idx) => {
    const lordship = evaluateParashariLordship(planet, lagnaSign);
    const isMaleficOrDusthana = lordship.isFunctionalMalefic || lordship.isPureDusthanaLord;

    const dayLordIdx = (DAY_LORDS || []).indexOf(planet);
    const dayOfWeek = dayLordIdx >= 0 ? DAY_NAMES_EN[dayLordIdx] : 'Thursday';
    const dayOfWeekHi = dayLordIdx >= 0 ? DAY_NAMES_HI[dayLordIdx] : 'गुरुवार';

    // Find slots in 7-day schedule matching this planet's day and hora
    const optimalSlots: { date: string; timeWindow: string; horaPlanet: PlanetName }[] = [];

    sevenDaySchedule.forEach(day => {
      day.horas.forEach(hora => {
        if (hora.planet === planet && hora.isDayHora && optimalSlots.length < 3) {
          optimalSlots.push({
            date: day.date,
            timeWindow: `${hora.startTime} – ${hora.endTime}`,
            horaPlanet: planet
          });
        }
      });
    });

    const theme = HORA_THEMES[planet] || HORA_THEMES.Jupiter;

    // Timing vs Eligibility Separation:
    // Chaldean timing identifies the planetary hora window.
    // Remedy eligibility is independently evaluated: for functional malefics or pure dusthana lords,
    // gemstone wearing is explicitly contraindicated during this hora.
    // Disclose [UNCERTAINTY_BOUNDARY] and do not synthesize arbitrary substitute remedies.
    let category = theme.categories[0] || 'Vedic Mantra';
    let remedyNameEn = `${planet} Empowerment & Sacred Hora Activation`;
    let remedyNameHi = `${PLANET_NAMES_HI[planet]} संवर्धन एवं शुभ होरा अनुष्ठान`;
    let contraindicationWarning: string | undefined = undefined;

    if (isMaleficOrDusthana) {
      category = 'Hora Timing Window';
      remedyNameEn = `${planet} Hora Window (Gemstone Contraindicated)`;
      remedyNameHi = `${PLANET_NAMES_HI[planet]} होरा काल (रत्न वर्जित)`;
      contraindicationWarning = `[PROJECT_INTERPRETATION] [UNCERTAINTY_BOUNDARY] Timing vs Eligibility: ${planet} is a functional malefic or pure Dusthana lord for ${lagnaSign} Lagna under BPHS Ch. 34 lordship rules. Physical gemstone wearing or empowerment is withheld during this hora under project Anukul-vada policy (BPHS Ch. 34 defines functional lordship, while gemstone prohibition is an interpretive remedial convention). Classical rules identify Chaldean hora timing only; no personalized substitute remedy is synthesized.`;
    }

    alignedRemedies.push({
      remedyId: `rem_${planet.toLowerCase()}_${idx}`,
      remedyNameEn,
      remedyNameHi,
      planet,
      category,
      bestDayOfWeek: dayOfWeek,
      bestDayOfWeekHi: dayOfWeekHi,
      bestHoraPlanet: planet,
      bestTimeOfDayEn: `Morning Hora of ${planet} on ${dayOfWeek}`,
      bestTimeOfDayHi: `${dayOfWeekHi} को ${PLANET_NAMES_HI[planet]} की प्रातःकालीन होरा`,
      upcomingOptimalSlots: optimalSlots,
      contraindicationWarning
    });
  });

  return {
    currentActiveHora,
    todaySchedule,
    sevenDaySchedule,
    alignedRemedies
  };
}
